import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';

interface State {
  error: Error | null;
}

/**
 * Top-level render-error boundary. Without it, one component throwing during
 * render unmounts the whole React tree and leaves a blank page.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled render error', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
        <EmptyState
          icon={AlertTriangle}
          title="Something went wrong"
          body="This page hit an unexpected error. Try again, or reload the app."
          action={
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => this.setState({ error: null })}>
                Try again
              </Button>
              <Button variant="primary" onClick={() => window.location.reload()}>
                Reload
              </Button>
            </div>
          }
        />
      </div>
    );
  }
}
